import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography
} from "@mui/material";
import {
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  Search as SearchIcon, Undo as UndoIcon, CheckCircle as CheckCircleIcon,
  Logout as LogoutIcon, UploadFile as UploadFileIcon, ClearAll as ClearAllIcon
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchDealerData, updateDealerData } from "../../api/apiConfig";
import { DealerData } from "../../models/dbupdate-model/dealerDataUpdate";
import { DEALER_SECTION_FIELDS } from "../../models/dbupdate-model/dealerDataUpdateSections";
import { logout } from "../../Redux/LoginAction";

export default function DealerDataUpdate() {
  const navigate = useNavigate();
  const [dealerNumber, setDealerNumber] = useState("");
  const [dealerData, setDealerData] = useState<DealerData | null>(null);
  const [formData, setFormData] = useState<DealerData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const changedFields = useMemo(() => {
    if (!dealerData || !formData) return new Set<keyof DealerData>();
    const changed = new Set<keyof DealerData>();
    for (const key of Object.keys(dealerData) as (keyof DealerData)[]) {
      if ((formData[key] ?? "") !== (dealerData[key] ?? "")) {
        changed.add(key);
      }
    }
    return changed;
  }, [dealerData, formData]);

  const hasChanges = changedFields.size > 0;

  useEffect(() => {
    const userDetail = localStorage.getItem("userdetail");
    const authToken = localStorage.getItem("authToken");
    if (!userDetail && !authToken) {
      navigate("/auth");
    }
  }, [navigate]);

  const handleFetchDealer = async () => {
    const dealerNum = dealerNumber.trim();
    if (!dealerNum) {
      toast.error("Please enter a dealer number");
      return;
    }

    setIsLoading(true);

    try {
      const responseData = await fetchDealerData(dealerNum);
      const success = Boolean(responseData.success ?? responseData.succeeded);

      if (success && responseData.data) {
        setDealerData(responseData.data);
        setFormData(responseData.data);
        setIsEditing(false);
        toast.success(responseData.message || "Dealer data loaded successfully");
      } else {
        setDealerData(null);
        setFormData(null);
        toast.error(responseData.message || "Failed to load dealer data");
      }
    } catch (error: any) {
      setDealerData(null);
      setFormData(null);
      const status = error?.response?.status;

      if (status === 404) {
        toast.error("Dealer not found");
      } else if (status === 401) {
        toast.error("Unauthorized. Please log in again.");
        navigate("/auth");
      } else if (status === 400) {
        toast.error(error?.response?.data?.message || "Invalid dealer number");
      } else {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred while fetching dealer data"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof DealerData, value: string) => {
    if (!formData) return;
    setFormData({ ...formData, [field]: value });
  };

  const handleResetField = (field: keyof DealerData) => {
    if (!formData || !dealerData) return;
    setFormData({ ...formData, [field]: dealerData[field] });
  };

  const validateForm = useCallback((): string[] => {
    if (!formData) return ["No data to validate"];
    const errors: string[] = [];
    if (formData.phone && !/^[\d\s()+-]{7,20}$/.test(formData.phone)) {
      errors.push("Phone number format is invalid");
    }
    if (formData.zip && !/^\d{5}(-\d{4})?$/.test(formData.zip)) {
      errors.push("ZIP code must be 5 digits (or 5+4 format)");
    }
    if (formData.zip1 && !/^\d{5}(-\d{4})?$/.test(formData.zip1)) {
      errors.push("Mailing ZIP code must be 5 digits (or 5+4 format)");
    }
    if (formData.st && formData.st.length > 2) {
      errors.push("State should be a 2-letter code");
    }
    if (formData.st1 && formData.st1.length > 2) {
      errors.push("Mailing State should be a 2-letter code");
    }
    return errors;
  }, [formData]);

  const handleSaveClick = () => {
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach((e) => toast.error(e));
      return;
    }
    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }
    setConfirmOpen(true);
  };

  const handleUpdateDealer = async () => {
    setConfirmOpen(false);
    if (!formData?.dlrNum || !dealerData?.dlrNum) {
      toast.error("No dealer data available for update");
      return;
    }

    setIsLoading(true);

    try {
      const responseData = await updateDealerData(dealerData.dlrNum, formData);
      const success = Boolean(responseData.success ?? responseData.succeeded);

      if (success) {
        const updatedData = responseData.data || formData;
        setDealerData(updatedData);
        setFormData(updatedData);
        setIsEditing(false);
        toast.success(responseData.message || "Dealer data updated successfully");
      } else {
        toast.error(responseData.message || "Failed to update dealer data");
      }
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 404) {
        toast.error("Dealer not found");
      } else if (status === 401) {
        toast.error("Unauthorized. Please log in again.");
        navigate("/auth");
      } else if (status === 400) {
        toast.error(error?.response?.data?.message || "Validation error");
      } else {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred while updating dealer data"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(dealerData);
    setIsEditing(false);
  };

  const handleLogout = () => {
    logout(navigate);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Dealer Data Update
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Coming Soon — Bulk upload dealer data via Excel">
            <span>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled
              >
                Upload Excel
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </Stack>
      </Stack>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label="Dealer Number"
              value={dealerNumber}
              onChange={(e) => setDealerNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleFetchDealer()}
              fullWidth
              size="small"
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleFetchDealer}
              disabled={isLoading}
              sx={{ minWidth: 140 }}
              startIcon={<SearchIcon />}
            >
              {isLoading && !isEditing ? "Loading..." : "Search"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setDealerNumber("");
                setDealerData(null);
                setFormData(null);
                setIsEditing(false);
              }}
              startIcon={<ClearAllIcon />}
            >
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Dealer Details Section */}
      {formData && (
        <Card>
          <CardContent>
            {/* Header with edit/save controls */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">Dealer Details</Typography>
                {isEditing && hasChanges && (
                  <Chip
                    label={`${changedFields.size} field${changedFields.size > 1 ? "s" : ""} modified`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
              {!isEditing ? (
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(true)}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleCancel}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveClick}
                    disabled={isLoading || !hasChanges}
                    startIcon={isLoading ? undefined : <SaveIcon />}
                  >
                    {isLoading ? "Saving..." : "Save Changes"}
                  </Button>
                </Stack>
              )}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Section Fields */}
            {DEALER_SECTION_FIELDS.map((section) => (
              <Box key={section.title} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    color: "primary.main",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    pb: 0.5,
                  }}
                >
                  {section.title}
                </Typography>
                <Grid container spacing={2}>
                  {section.fields.map((field) => {
                    const isChanged = changedFields.has(field.key);
                    const isDisabled = !isEditing || Boolean(field.disabled);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <TextField
                          label={field.label}
                          value={formData[field.key] ?? ""}
                          onChange={(e) =>
                            handleInputChange(field.key, e.target.value)
                          }
                          fullWidth
                          size="small"
                          disabled={isDisabled}
                          sx={{
                            "& .MuiOutlinedInput-root": isChanged
                              ? {
                                  "& fieldset": {
                                    borderColor: "warning.main",
                                    borderWidth: 2,
                                  },
                                }
                              : {},
                          }}
                          InputProps={{
                            endAdornment:
                              isEditing && isChanged ? (
                                <InputAdornment position="end">
                                  <Tooltip title="Undo change">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleResetField(field.key)}
                                    >
                                      <UndoIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ) : undefined,
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to update <strong>{changedFields.size}</strong> field
            {changedFields.size > 1 ? "s" : ""} for dealer{" "}
            <strong>{formData?.dlrNum}</strong>:
          </Typography>
          <Stack spacing={1}>
            {Array.from(changedFields).map((fieldKey) => {
              const sectionField = DEALER_SECTION_FIELDS
                .flatMap((s) => s.fields)
                .find((f) => f.key === fieldKey);
              return (
                <Box
                  key={fieldKey}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <CheckCircleIcon color="warning" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {sectionField?.label || fieldKey}:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textDecoration: "line-through" }}
                  >
                    {dealerData?.[fieldKey] || "(empty)"}
                  </Typography>
                  <Typography variant="body2">→</Typography>
                  <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                    {formData?.[fieldKey] || "(empty)"}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateDealer}>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1500,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
